import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { SelectField } from "@/lib/crm";
import { fmtCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { paymentLabel } from "@/components/sales/PaymentFields";


export const BulkFeesDialog = ({ open, onClose }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [sel, setSel] = useState([]);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), numeration: "", payment_method_id: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!open) { return; }
    setData(null); setRes(null);
    api.get("/fic/invoices/bulk-fees").then((r) => { setData(r.data); setSel(r.data.customers.filter((c) => !c.missing.length).map((c) => c.company_id)); })
      .catch((e) => { toast.error(errorMessage(e)); onClose(); });
    api.get("/fic/info").then((r) => setMeta(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/fic/invoices/bulk-fees", { company_ids: sel, date: f.date, numeration: f.numeration || null,
        payment_method_id: f.payment_method_id ? Number(f.payment_method_id) : null })).data;
      setRes(r); toast.success(`${r.created} fatture create in bozza`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const total = (data?.customers || []).filter((c) => sel.includes(c.company_id)).reduce((s, c) => s + c.taxable, 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="bulk-fees-dialog">
        <DialogHeader><DialogTitle>Fattura tutti i canoni maturati</DialogTitle>
          <DialogDescription>Una fattura in bozza per cliente su Fatture in Cloud; scadenza secondo le condizioni di pagamento dell'offerta/ordine. L'invio allo SDI resta da confermare fattura per fattura.</DialogDescription></DialogHeader>
        {!data ? <p className="py-6 text-center text-sm text-slate-500">Caricamento…</p> : res ? (
          <ul className="divide-y divide-line text-sm" data-testid="bulk-fees-results">{res.results.map((r) => (
            <li key={r.company_id} className="flex justify-between gap-3 py-2"><span className="font-medium text-ink">{r.company_name}</span>
              {r.ok ? <span className="text-green-700">Fattura {r.number} · {fmtCurrency(r.taxable)}</span> : <span className="text-right text-red-600">{r.error}</span>}</li>))}</ul>
        ) : !data.customers.length ? <p className="py-6 text-center text-sm text-slate-500" data-testid="bulk-fees-empty">Nessun canone da fatturare.</p> : <>
          <ul className="divide-y divide-line rounded-control border border-line text-sm">{data.customers.map((c) => (
            <li key={c.company_id} className="flex items-start gap-3 px-3 py-2" data-testid={`bulk-fees-customer-${c.company_id}`}>
              <Checkbox className="mt-0.5" checked={sel.includes(c.company_id)} disabled={c.missing.length > 0}
                onCheckedChange={(v) => setSel((s) => (v ? [...s, c.company_id] : s.filter((x) => x !== c.company_id)))} data-testid={`bulk-fees-select-${c.company_id}`} />
              <div className="min-w-0 flex-1"><p className="font-medium text-ink">{c.company_name}</p>
                <p className="text-xs text-slate-500">{c.items.length} canoni · {c.items.map((i) => i.number).join(", ")} · {paymentLabel(c.payment_terms) || "Rimessa diretta"}</p>
                {c.missing.length > 0 && <p className="text-xs text-red-600">Saltato: mancano {c.missing.join(", ")}</p>}</div>
              <span className="whitespace-nowrap font-semibold">{fmtCurrency(c.taxable)}</span></li>))}</ul>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label className="text-xs">Data fattura</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="bulk-fees-date" /></div>
            <SelectField label="Metodo di pagamento" value={f.payment_method_id} onChange={(v) => setF({ ...f, payment_method_id: v })} allowNone={(() => { const d = (meta?.payment_methods || []).find((p) => p.is_default); return d ? `Predefinito (${d.name})` : "Predefinito"; })()}
              options={(meta?.payment_methods || []).map((p) => ({ key: String(p.id), label: p.name }))} testId="bulk-fees-payment" />
            <SelectField label="Sezionale" value={f.numeration} onChange={(v) => setF({ ...f, numeration: v })} allowNone="Principale"
              options={(meta?.numerations || []).filter(Boolean).map((n) => ({ key: n, label: n }))} testId="bulk-fees-numeration" />
          </div>
          <p className="text-right text-sm">{sel.length} clienti · imponibile <b>{fmtCurrency(total)}</b> + IVA</p>
        </>}
        <DialogFooter>{res ? <Button onClick={() => { onClose(); nav("/fatture"); }} data-testid="bulk-fees-open-invoices">Vai alle fatture</Button> : <>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button disabled={busy || !sel.length} onClick={go} data-testid="bulk-fees-submit">{busy ? "Creazione in corso…" : `Crea ${sel.length} fatture in bozza`}</Button></>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
